import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

/**
 * Columns that can be filled from a spreadsheet paste, in left-to-right table order.
 * The paste anchors at one of these columns and fills rightward across the list.
 */
export const PACKING_LIST_PASTE_COLUMNS = ["length", "width", "height", "weight_piece", "units"] as const;

export type PackingListPasteColumn = typeof PACKING_LIST_PASTE_COLUMNS[number];

/**
 * Parses clipboard text copied from a spreadsheet into a 2D grid of raw cell strings.
 * Rows are tab-delimited, separated by newlines. A single trailing empty line (which
 * spreadsheets commonly append) is dropped. Returns `[]` when the text contains no tab
 * or newline, signalling that this is a plain single value, not a grid.
 */
export function parseClipboardGrid(text: string): string[][] {
  if (!/[\t\r\n]/.test(text)) return [];

  const lines = text.split(/\r\n|\n|\r/);
  while (lines.length > 1 && lines[lines.length - 1] === "") {
    lines.pop();
  }

  return lines.map((line) => line.split("\t"));
}

/**
 * Normalizes a single pasted cell into the numeric value stored on a packing-list row.
 * Blank cells clear the field (`null`); thousands separators are stripped; non-numeric
 * cells are ignored (`null`).
 */
export function normalizePastedCell(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (trimmed === "") return null;

  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function collectHandlingUnitIndices(rows: PackingListRow[]): number[] {
  return rows.reduce<number[]>((acc, row, index) => {
    if (!isContainerRow(row)) acc.push(index);
    return acc;
  }, []);
}

/**
 * Returns a new packing-list array with the pasted grid applied to the handling-unit rows.
 *
 * Filling starts at `anchorIndex` / `anchorColumn` and proceeds rightward across
 * {@link PACKING_LIST_PASTE_COLUMNS} and downward across subsequent handling-unit rows.
 * Container rows are skipped. New handling-unit rows are appended (via `makeRow`) when the
 * grid has more rows than exist. A trailing empty handling-unit row is appended afterwards
 * so the table always ends with a blank row, matching the manual-entry behaviour.
 *
 * The input array is never mutated.
 */
export function applyPastedGrid(
  rows: PackingListRow[],
  anchorIndex: number,
  anchorColumn: PackingListPasteColumn,
  grid: string[][],
  makeRow: () => PackingListRow,
): PackingListRow[] {
  const next = rows.map((row) => ({ ...row }));

  const anchorColIdx = PACKING_LIST_PASTE_COLUMNS.indexOf(anchorColumn);
  if (anchorColIdx === -1) return next;

  const handlingUnitIndices = collectHandlingUnitIndices(next);
  const anchorPos = handlingUnitIndices.indexOf(anchorIndex);
  if (anchorPos === -1) return next;

  grid.forEach((cells, rowOffset) => {
    let targetIndex = handlingUnitIndices[anchorPos + rowOffset];
    if (targetIndex === undefined) {
      next.push(makeRow());
      targetIndex = next.length - 1;
      handlingUnitIndices.push(targetIndex);
    }

    cells.forEach((cell, cellOffset) => {
      const colIdx = anchorColIdx + cellOffset;
      if (colIdx >= PACKING_LIST_PASTE_COLUMNS.length) return;

      const field = PACKING_LIST_PASTE_COLUMNS[colIdx];
      next[targetIndex] = { ...next[targetIndex], [field]: normalizePastedCell(cell) };
    });
  });

  const finalHandlingUnitIndices = collectHandlingUnitIndices(next);
  const lastHandlingUnitIndex = finalHandlingUnitIndices[finalHandlingUnitIndices.length - 1];
  if (lastHandlingUnitIndex !== undefined && !isPackingListRowEmpty(next[lastHandlingUnitIndex])) {
    next.push(makeRow());
  }

  return next;
}
